import type { ReactNode } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { BALANCE_GRADIENT } from '../../styles/chowGradients'
import { naira } from '../../lib/format'

/* ── BalanceCard ─────────────────────────────────────────────────────── */

const BalanceStyle = {
  Card: styled.section`
    color: ${minimalVars.color.textInverse};
    border-radius: 22px;
    padding: ${minimalVars.space.sm};
    display: grid;
    gap: 10px;
    box-shadow: var(--chow-shadow-balance, 0 4px 16px -4px rgba(0, 0, 0, 0.35));

    background: ${BALANCE_GRADIENT};
    border: 1px solid rgba(255,255,255,0.18);
    &[data-embedded] {
      background: rgba(255,255,255,0.08);
      border: 1px solid rgba(255,255,255,0.22);
    }

    /* 520px is between the hand and page stops, so it stays hand-typed. */
    @media (max-width: 520px) {
      padding: 14px 16px;
      gap: 8px;
    }
  `,
  Label: styled.span`
    font-size: ${minimalVars.typography.metaSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.75);
  `,
  Amount: styled.strong`
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: ${minimalVars.typography.weightSemibold};
    /* The balance stays fixed: h1Size grows to 40px and would invert the
       520px step down to 1.85rem below it. */
    font-size: 2.2rem;
    line-height: 1;
    letter-spacing: -0.02em;

    /* 520px is between the hand and page stops, so it stays hand-typed. */
    @media (max-width: 520px) {
      font-size: 1.85rem;
    }
  `,
  Actions: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 4px;
  `,
  Action: styled.button`
    cursor: pointer;
    font: inherit;
    font-weight: ${minimalVars.typography.weightSemibold};
    font-size: ${minimalVars.typography.captionSize};
    padding: 8px 14px;
    border-radius: 10px;
    transition: filter 160ms ease, transform 120ms ease;
    text-align: center;

    &[data-variant="solid"] {
      background: ${minimalVars.color.brand};
      color: ${minimalVars.color.textInverse};
      border: 1px solid ${minimalVars.color.brand};
    }
    &[data-variant="outline"] {
      background: rgba(255,255,255,0.12);
      color: ${minimalVars.color.textInverse};
      border: 1px solid rgba(255,255,255,0.35);
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        filter: brightness(1.08);
        transform: translateY(-1px);
      }
    }
    &:focus-visible {
      outline: 2px solid ${minimalVars.color.textInverse};
      outline-offset: 2px;
    }

    /* 520px is between the hand and page stops, so it stays hand-typed. */
    @media (max-width: 520px) {
      padding: 7px 10px;
      font-size: ${minimalVars.typography.metaSize};
    }
  `,
}

export interface BalanceCardProps {
  balance: number
  /** Optional primary action — usually "Top Up" (customer) or "Cashout" (chef). */
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean }
  onHistory?: () => void
  /** Legacy: customer top-up callback. Wraps to a primaryAction labelled "+ Top Up". */
  onTopUp?: () => void
  /** Override the balance label (e.g. "Earnings available" for chefs). */
  label?: string
  /** Drop the maroon fill so the card sits inside a maroon header. */
  embedded?: boolean
}

export const BalanceCard = ({
  balance,
  primaryAction,
  onTopUp,
  onHistory,
  label = 'Available balance',
  embedded = false,
}: BalanceCardProps) => {
  const action = primaryAction ?? (onTopUp ? { label: '+ Top Up', onClick: onTopUp } : undefined)
  return (
    <BalanceStyle.Card data-embedded={embedded || undefined}>
      <BalanceStyle.Label>{label}</BalanceStyle.Label>
      <BalanceStyle.Amount>{naira(balance)}</BalanceStyle.Amount>
      <BalanceStyle.Actions>
        {action ? (
          <BalanceStyle.Action
            type="button"
            data-variant="solid"
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </BalanceStyle.Action>
        ) : null}
        <BalanceStyle.Action type="button" data-variant="outline" onClick={onHistory}>
          History
        </BalanceStyle.Action>
      </BalanceStyle.Actions>
    </BalanceStyle.Card>
  )
}

/* ── StatTile ────────────────────────────────────────────────────────── */

const StatCard = styled.div`
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  padding: 10px 12px;
  border-radius: 18px;
  box-shadow: ${minimalVars.shadow.subtle};
`

const StatStyle = {
  Body: styled.div`
    display: grid;
    justify-items: center;
    gap: 3px;
    text-align: center;
  `,
  Glyph: styled.span`
    /* A decorative emoji glyph, so it keeps a fixed size off the type scale. */
    font-size: 1.4rem;
    line-height: 1;
  `,
  Label: styled.span`
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.metaSize};
  `,
  Value: styled.strong`
    font-family: ${minimalVars.typography.monoFamily};
    font-weight: ${minimalVars.typography.weightMedium};
    /* Mono figures stay at 1.15rem: h2Size is 22px and overflows a 3-up row. */
    font-size: 1.15rem;
    color: ${minimalVars.color.textPrimary};
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum' 1;
  `,
}

export interface StatTileProps {
  glyph: ReactNode
  label: ReactNode
  amount: number
}

export const StatTile = ({ glyph, label, amount }: StatTileProps) => (
  <StatCard>
    <StatStyle.Body>
      <StatStyle.Glyph aria-hidden>{glyph}</StatStyle.Glyph>
      <StatStyle.Label>{label}</StatStyle.Label>
      <StatStyle.Value>{naira(amount)}</StatStyle.Value>
    </StatStyle.Body>
  </StatCard>
)

/* ── TransactionRow ──────────────────────────────────────────────────── */

const TxnCard = styled.div`
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  padding: 8px 12px;
  border-radius: 14px;
  box-shadow: ${minimalVars.shadow.subtle};
`

const TxnStyle = {
  Row: styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  Icon: styled.div<{ $in: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${minimalVars.typography.bodySize};
    background: ${({ $in }) => ($in ? minimalVars.color.successSoft : minimalVars.color.brandSoft)};
  `,
  Copy: styled.div`
    display: grid;
    gap: 1px;
    min-width: 0;
    flex: 1;
  `,
  Title: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textPrimary};
  `,
  Sub: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.metaSize};
  `,
  Amount: styled.span<{ $in: boolean }>`
    flex-shrink: 0;
    font-family: ${minimalVars.typography.monoFamily};
    font-weight: ${minimalVars.typography.weightMedium};
    font-size: ${minimalVars.typography.captionSize};
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum' 1;
    color: ${({ $in }) => ($in ? minimalVars.color.success : minimalVars.color.brand)};
  `,
}

export interface TransactionRowProps {
  glyph: ReactNode
  title: ReactNode
  sub: ReactNode
  amount: number
  direction: 'in' | 'out'
}

export const TransactionRow = ({ glyph, title, sub, amount, direction }: TransactionRowProps) => {
  const isIn = direction === 'in'
  return (
    <TxnCard>
      <TxnStyle.Row>
        <TxnStyle.Icon $in={isIn} aria-hidden>
          {glyph}
        </TxnStyle.Icon>
        <TxnStyle.Copy>
          <TxnStyle.Title>{title}</TxnStyle.Title>
          <TxnStyle.Sub>{sub}</TxnStyle.Sub>
        </TxnStyle.Copy>
        <TxnStyle.Amount $in={isIn}>
          {isIn ? '+' : '−'}
          {naira(amount)}
        </TxnStyle.Amount>
      </TxnStyle.Row>
    </TxnCard>
  )
}
